import { ReactElement } from 'react';
import ChartCard from '../../../components/ChartCard';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapAutoResize } from '../../../components/MapBoundsComponents';
import { DivIcon, LatLngExpression } from 'leaflet';

// Builds a teardrop gate marker: a coloured dot, a stem, and a rotated label
// chip. Roles (start/split/finish) only change the colour scheme.
function gateIcon(label: string, dotColor: string, labelBg: string, labelColor: string): DivIcon {
  return new DivIcon({
    html: `
      <div style="position: relative; width: 100px; height: 100px;">
        <div style="position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);">
          <div style="width: 8px; height: 8px; background-color: ${dotColor}; border-radius: 50%;
            border: 2px solid white;"></div>
        </div>
        <div style="position: absolute; left: 50%; bottom: 6px; transform: translateX(-50%);
          width: 2px; height: 30px; background-color: ${dotColor}; opacity: 0.6;"></div>
        <div style="position: absolute; left: 50%; bottom: 36px; transform: translateX(-50%);
          background-color: ${labelBg}; color: ${labelColor}; padding: 4px 12px; font-weight: bold;
          font-size: 12px; border-radius: 4px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          transform: translateX(-50%) rotate(-45deg); white-space: nowrap; opacity: 0.6;">
          ${label}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 100],
    iconAnchor: [50, 94],
  });
}

// Colour the first gate light (start), the last gate dark (finish), and any
// intermediate split gates a neutral mid-tone.
function iconForGate(label: string, index: number, count: number): DivIcon {
  if (index === 0) return gateIcon(label, '#BBD5DE', '#BBD5DE', '#03365D');
  if (index === count - 1) return gateIcon(label, '#03365D', '#03365D', 'white');
  return gateIcon(label, '#6B8FA3', '#6B8FA3', 'white');
}

export default function RunMap(props: {
  lats: number[];
  lons: number[];
  gates_lat?: number[] | null;
  gates_lon?: number[] | null;
}): ReactElement {
  const { lats, lons, gates_lat, gates_lon } = props;
  const raw_lats: number[] = lats || [];
  const nonempty_lats = raw_lats.filter((i) => i);
  const raw_lons: number[] = lons || [];
  const nonempty_longs = raw_lons.filter((i) => i);
  const coordinates: LatLngExpression[] = nonempty_lats.map((lat: number, index: any) => {
    const long = nonempty_longs[index];
    return [lat, long];
  });

  if (coordinates.length === 0) {
    return <div>This run has no coordinates</div>;
  }

  // One marker per session gate. Gates A, B, C… are read from the parallel
  // gates_lat / gates_lon arrays (run_health_obj.sensors_present.gates).
  const gatePoints: { lat: number; lon: number }[] =
    gates_lat && gates_lon
      ? gates_lat
          .map((lat, i) => ({ lat, lon: gates_lon[i] }))
          .filter((g) => Number.isFinite(g.lat) && Number.isFinite(g.lon))
      : [];

  const markers: { pos: LatLngExpression; label: string; icon: DivIcon }[] =
    gatePoints.length > 0
      ? gatePoints.map((g, i) => {
          const label = `Gate ${String.fromCharCode(65 + i)}`;
          return { pos: [g.lat, g.lon] as LatLngExpression, label, icon: iconForGate(label, i, gatePoints.length) };
        })
      : [
          { pos: coordinates[0], label: 'Start', icon: gateIcon('Start', '#BBD5DE', '#BBD5DE', '#03365D') },
          {
            pos: coordinates[coordinates.length - 1],
            label: 'Finish',
            icon: gateIcon('Finish', '#03365D', '#03365D', 'white'),
          },
        ];

  // Centre on the mean of the gate positions when we have them, otherwise on
  // the start of the GPS track.
  const mapCenter: LatLngExpression =
    gatePoints.length > 0
      ? [
          gatePoints.reduce((s, g) => s + g.lat, 0) / gatePoints.length,
          gatePoints.reduce((s, g) => s + g.lon, 0) / gatePoints.length,
        ]
      : coordinates[0];

  return (
    <ChartCard className="cr-h-96 cr-overflow-hidden">
      <MapContainer center={mapCenter} zoom={15} scrollWheelZoom={false}>
        <MapAutoResize />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {markers.map((m, i) => (
          <Marker key={i} position={m.pos} icon={m.icon}>
            <Popup>{m.label}</Popup>
          </Marker>
        ))}
        <Polyline pathOptions={{ color: 'purple' }} positions={coordinates} />
      </MapContainer>
    </ChartCard>
  );
}
